import React, { useState } from "react";

/**
 * Ligne d'un article — checkbox, nom, quantité, propriétaire(s), suppression.
 * canCheck et canDelete contrôlent les permissions (admin-only sur la liste commune).
 */
function ShoppingItem({ item, onCheck, onDelete, onEdit, showOwner, canCheck = true, canDelete = true, categories }) {
  const [isEditing, setIsEditing] = useState(false);
  const [justChecked, setJustChecked] = useState(false);
  const [editName, setEditName] = useState(item.name);
  const [editQuantity, setEditQuantity] = useState(item.quantity || "1");
  const [editCategory, setEditCategory] = useState(item.category || "Autre");

  const handleStartEdit = (e) => {
    e.stopPropagation();
    setEditName(item.name);
    setEditQuantity(item.quantity || "1");
    setEditCategory(item.category || "Autre");
    setIsEditing(true);
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!editName.trim()) return;
    try {
      await onEdit(item.id, {
        name: editName.trim(),
        quantity: editQuantity.trim() || "1",
        category: editCategory,
      });
      setIsEditing(false);
    } catch (err) {
      console.error("Erreur modification:", err);
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave(e);
    if (e.key === "Escape") handleCancel(e);
  };

  // Mode édition inline
  if (isEditing) {
    return (
      <li className="shopping-item editing">
        <div className="edit-fields">
          <input
            className="edit-input edit-name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nom de l'article"
            aria-label="Nom de l'article"
            autoFocus
          />
          <input
            className="edit-input edit-quantity"
            value={editQuantity}
            onChange={(e) => setEditQuantity(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Qté"
            aria-label="Quantité"
          />
          {categories && categories.length > 0 && (
            <select
              className="edit-input edit-category"
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}
        </div>
        <div className="edit-actions">
          <button className="edit-btn save" onClick={handleSave} title="Sauvegarder">
            &#x2714;
          </button>
          <button className="edit-btn cancel" onClick={handleCancel} title="Annuler">
            &#x2716;
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className={`shopping-item ${item.checked ? "checked" : ""}`}>
      {canCheck && onCheck && (
        <button
          className={`item-checkbox${justChecked ? " just-checked" : ""}`}
          onClick={() => {
            setJustChecked(true);
            setTimeout(() => setJustChecked(false), 300);
            onCheck(item);
          }}
          aria-label={item.checked ? `Décocher ${item.name}` : `Cocher ${item.name}`}
          title={item.checked ? "Décocher" : "Cocher"}
        >
          {item.checked ? "\u2705" : "\u2B1C"}
        </button>
      )}

      {/* Indicateur visuel lecture seule (pas de checkbox) */}
      {!canCheck && (
        <span className="item-status-icon" aria-hidden="true">
          {item.checked ? "\u2705" : "\u2B1C"}
        </span>
      )}

      <div className="item-info" onClick={onEdit ? handleStartEdit : undefined}
           style={onEdit ? { cursor: "pointer" } : undefined}
           title={onEdit ? "Cliquer pour modifier" : undefined}>
        <span className="item-name">{item.name}</span>
        {item.quantity && item.quantity !== "1" && (
          <span className="item-quantity">{item.quantity}</span>
        )}
      </div>

      {/* Multi-owners (articles agrégés dans la liste commune) */}
      {showOwner && item.users && (
        <div className="item-owners">
          {item.users.map((u) => (
            <span
              key={u.id}
              className="item-owner"
              style={{ backgroundColor: `${u.color}25`, color: u.color }}
              title={`Ajouté par ${u.username}`}
            >
              {u.emoji}
            </span>
          ))}
        </div>
      )}

      {/* Single owner */}
      {showOwner && item.user && !item.users && (
        <span
          className="item-owner"
          style={{ backgroundColor: `${item.user.color}25`, color: item.user.color }}
          title={`Ajouté par ${item.user.username}`}
        >
          {item.user.emoji}
        </span>
      )}

      {onEdit && !item.checked && (
        <button className="item-edit" onClick={handleStartEdit}
          aria-label={`Modifier ${item.name}`} title="Modifier">
          &#x270F;
        </button>
      )}

      {canDelete && onDelete && (
        <button
          className="item-delete"
          onClick={() => onDelete(item.my_ids ? item : item.id)}
          aria-label={`Supprimer ${item.name}`}
          title="Supprimer"
        >
          &#x1F5D1;
        </button>
      )}
    </li>
  );
}

export default ShoppingItem;
